import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Collapse,
  CircularProgress,
  Divider,
  Fade,
  Tooltip
} from '@mui/material';
import {
  AutoAwesome,
  Send,
  Lightbulb,
  CheckCircle,
  Psychology,
  ExpandMore,
  ExpandLess,
  Edit,
  Close
} from '@mui/icons-material';
import { brand } from '../../theme/colors';

/**
 * Verifier AI Profile Assistant Component
 * Provides conversational AI assistance for building and optimizing verifier profiles
 * Matches the conversational AI pattern used throughout the platform
 */
const VerifierAIProfileAssistant = ({ profileData, onProfileUpdate, onSuggestionApply }) => {
  const [expanded, setExpanded] = useState(true);
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeMode, setActiveMode] = useState(null); // 'bio', 'skills', 'general'
  const messagesEndRef = useRef(null);

  // Initialize with welcome message
  useEffect(() => {
    const welcomeMessage = {
      role: 'assistant',
      content: `Hi ${profileData?.personalInfo?.firstName || 'there'}! 👋\n\nI can help you create a professional verifier bio in just 2 minutes. I'll ask you a few questions about your background and expertise, then generate a compelling bio.\n\nI can also suggest relevant certifications and review your profile.\n\nWhat would you like to start with?`,
      timestamp: new Date(),
      suggestions: [
        { text: '✍️ Write my bio (2 min)', action: 'bio' },
        { text: '📜 Suggest certifications', action: 'certifications' },
        { text: '✅ Review my profile', action: 'review' }
      ]
    };
    setMessages([welcomeMessage]);
    
    // Generate initial suggestions based on profile completeness
    generateSuggestions();
  }, [profileData]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const generateSuggestions = () => {
    const newSuggestions = [];
    
    // Check profile completeness
    if (!profileData?.personalInfo?.bio || profileData.personalInfo.bio.length < 50) {
      newSuggestions.push({
        type: 'warning',
        title: 'Complete your bio',
        description: 'A detailed bio helps clients understand your expertise',
        action: 'bio',
        priority: 'high'
      });
    }
    
    if (!profileData?.skills || profileData.skills.length < 3) {
      newSuggestions.push({
        type: 'info',
        title: 'Add more skills',
        description: 'Showcase at least 5 skills to improve your profile visibility',
        action: 'skills',
        priority: 'high'
      });
    }
    
    if (!profileData?.portfolio || profileData.portfolio.length === 0) {
      newSuggestions.push({
        type: 'info',
        title: 'Add portfolio items',
        description: 'Photos of your work help clients see your quality',
        action: 'portfolio',
        priority: 'medium'
      });
    }
    
    if (!profileData?.certifications || profileData.certifications.length === 0) {
      newSuggestions.push({
        type: 'info',
        title: 'Add certifications',
        description: 'Certifications build trust and credibility',
        action: 'certifications',
        priority: 'low'
      });
    }
    
    setSuggestions(newSuggestions);
  };

  const handleSendMessage = async () => {
    if (!currentInput.trim()) return;
    
    const userMessage = {
      role: 'user',
      content: currentInput,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setCurrentInput('');
    setLoading(true);
    
    // Simulate AI response (replace with actual AI service call)
    setTimeout(() => {
      const response = generateAIResponse(currentInput, activeMode);
      setMessages(prev => [...prev, response]);
      setLoading(false);
    }, 1000);
  };

  const generateAIResponse = (input, mode) => {
    const lowerInput = input.toLowerCase();
    
    // Bio writing mode
    if (mode === 'bio' || lowerInput.includes('bio') || lowerInput.includes('about me')) {
      return {
        role: 'assistant',
        content: `Great! Let's craft a compelling bio. Tell me:\n\n1. What's your main specialty? (e.g., foundation work, roofing, plumbing)\n2. How many years of experience do you have?\n3. What makes you different from other contractors?\n4. Any notable projects or achievements?\n\nJust answer naturally, and I'll write a professional bio for you!`,
        timestamp: new Date(),
        mode: 'bio'
      };
    }
    
    // Skills suggestion mode
    if (mode === 'skills' || lowerInput.includes('skill')) {
      const trade = profileData?.businessInfo?.businessType || 'construction';
      const suggestedSkills = getSkillsForTrade(trade);
      
      return {
        role: 'assistant',
        content: `Based on your profile, here are some skills that might be relevant to you:`,
        timestamp: new Date(),
        skillSuggestions: suggestedSkills,
        mode: 'skills'
      };
    }
    
    // Profile review mode
    if (mode === 'review' || lowerInput.includes('review') || lowerInput.includes('check')) {
      const completeness = calculateProfileCompleteness();
      return {
        role: 'assistant',
        content: `I've reviewed your profile! Here's what I found:\n\n✅ Profile completeness: ${completeness}%\n\n${getProfileReview()}`,
        timestamp: new Date(),
        mode: 'review'
      };
    }
    
    // General response
    return {
      role: 'assistant',
      content: `I can help you with that! Would you like me to:\n• Write or improve your bio\n• Suggest relevant skills\n• Review your profile for completeness\n• Give tips on attracting more clients`,
      timestamp: new Date(),
      suggestions: [
        { text: 'Write my bio', action: 'bio' },
        { text: 'Suggest skills', action: 'skills' },
        { text: 'Review profile', action: 'review' }
      ]
    };
  };

  const getSkillsForTrade = (trade) => {
    const skillsByTrade = {
      'construction': ['Foundation Work', 'Brickwork', 'Concrete', 'Roofing', 'Framing'],
      'plumbing': ['Pipe Installation', 'Drain Cleaning', 'Water Heater', 'Leak Repair', 'Bathroom Plumbing'],
      'electrical': ['Wiring', 'Circuit Installation', 'Panel Upgrades', 'Lighting', 'Troubleshooting'],
      'carpentry': ['Framing', 'Cabinet Making', 'Trim Work', 'Door Installation', 'Custom Woodwork'],
      'painting': ['Interior Painting', 'Exterior Painting', 'Drywall Repair', 'Staining', 'Wallpaper']
    };
    
    return skillsByTrade[trade.toLowerCase()] || skillsByTrade['construction'];
  };

  const calculateProfileCompleteness = () => {
    let score = 0;
    const checks = [
      profileData?.personalInfo?.bio && profileData.personalInfo.bio.length > 50,
      profileData?.personalInfo?.avatar,
      profileData?.skills && profileData.skills.length >= 3,
      profileData?.businessInfo?.yearsExperience,
      profileData?.portfolio && profileData.portfolio.length > 0,
      profileData?.certifications && profileData.certifications.length > 0,
      profileData?.personalInfo?.phone,
      profileData?.personalInfo?.location
    ];
    
    score = (checks.filter(Boolean).length / checks.length) * 100;
    return Math.round(score);
  };

  const getProfileReview = () => {
    const reviews = [];
    
    if (!profileData?.personalInfo?.bio || profileData.personalInfo.bio.length < 50) {
      reviews.push('⚠️ Your bio is too short. Aim for at least 100 words.');
    } else {
      reviews.push('✅ Your bio looks good!');
    }
    
    if (!profileData?.skills || profileData.skills.length < 3) {
      reviews.push('⚠️ Add more skills to improve visibility.');
    } else {
      reviews.push('✅ Good skill coverage!');
    }
    
    if (!profileData?.portfolio || profileData.portfolio.length === 0) {
      reviews.push('⚠️ Add portfolio photos to showcase your work.');
    } else {
      reviews.push('✅ Portfolio looks great!');
    }
    
    return reviews.join('\n');
  };

  const handleSuggestionClick = (action) => {
    setActiveMode(action);
    const message = {
      role: 'user',
      content: action === 'bio' ? 'Help me write my bio' : 
               action === 'skills' ? 'Suggest skills for my trade' : 
               'Review my profile',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, message]);
    
    setTimeout(() => {
      const response = generateAIResponse('', action);
      setMessages(prev => [...prev, response]);
    }, 500);
  };

  const handleSkillApply = (skill) => {
    if (onSuggestionApply) {
      onSuggestionApply('skill', skill);
    }
  };

  return (
    <Card sx={{ 
      mb: 3, 
      background: brand.gradient,
      color: 'white'
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
              <Psychology />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Profile Assistant
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Powered by AI • Real-time help
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setExpanded(!expanded)}
            sx={{ color: 'white' }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {/* Suggestions */}
          {suggestions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                Quick Actions:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {suggestions.map((suggestion, index) => (
                  <Chip
                    key={index}
                    icon={<Lightbulb sx={{ color: 'white !important' }} />}
                    label={suggestion.title}
                    onClick={() => handleSuggestionClick(suggestion.action)}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Chat Messages */}
          <Paper sx={{ 
            maxHeight: 300, 
            overflow: 'auto', 
            p: 2, 
            mb: 2,
            bgcolor: 'rgba(255,255,255,0.95)',
            color: 'text.primary'
          }}>
            {messages.map((message, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1
                }}>
                  <Paper sx={{ 
                    p: 1.5, 
                    maxWidth: '80%',
                    bgcolor: message.role === 'user' ? brand.primary : '#f5f5f5',
                    color: message.role === 'user' ? 'white' : 'text.primary'
                  }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {message.content}
                    </Typography>
                    
                    {/* Skill suggestions */}
                    {message.skillSuggestions && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {message.skillSuggestions.map((skill, idx) => (
                          <Chip
                            key={idx}
                            label={skill}
                            size="small"
                            onClick={() => handleSkillApply(skill)}
                            icon={<CheckCircle sx={{ fontSize: 16 }} />}
                          />
                        ))}
                      </Box>
                    )}
                    
                    {/* Action suggestions */}
                    {message.suggestions && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {message.suggestions.map((suggestion, idx) => (
                          <Button
                            key={idx}
                            size="small"
                            variant="outlined"
                            onClick={() => handleSuggestionClick(suggestion.action)}
                            sx={{ 
                              borderColor: message.role === 'user' ? 'white' : 'primary.main',
                              color: message.role === 'user' ? 'white' : 'primary.main'
                            }}
                          >
                            {suggestion.text}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Box>
            ))}
            
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  AI is thinking...
                </Typography>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Paper>

          {/* Input */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask me anything about your profile..."
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'transparent' }
                }
              }}
            />
            <IconButton 
              onClick={handleSendMessage}
              disabled={!currentInput.trim() || loading}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              <Send />
            </IconButton>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default VerifierAIProfileAssistant;
